import React, { useEffect, useState } from 'react';
import { WhiteLabelProvider } from '@metaforcelabs/metaforce-core';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import {
  RecoilRoot,
} from 'recoil';
import { AuthenticationProvider, oidcLog, WebStorageStateStore, useReactOidc } from '@axa-fr/react-oidc-context';
import { getUserManager } from '@axa-fr/react-oidc-core';
import {config, oidcConfig} from './utils/config';
import AdminLayout from "./layouts/AdminLayout"
import Oidc from "./screens/Oidc";
import { Toaster } from './toaster';
import { OidcSecure } from '@axa-fr/react-oidc-context'
import { Unauthorized } from './screens/Errors/Unauthorized';
import { NotFound } from './screens/Errors/NotFound';
import { NotAuthenticated } from './screens/Errors/NotAuthenticated';
import { setAuthHeader } from './api/api'
import MissingLicense from './screens/Errors/MissingLicense';
import { getUserProfile } from './api/userProfile';
import { OidcRoutesContext } from './contexts';
import { getCustomerInfo } from './api/customer';
import { useToastAction } from './hooks/useToastAction';
import Send from './screens/Admin/Send';
import Home from './screens/Admin/Home'
import SendFlow from './screens/Admin/Send/sendFlow';
import Document from './screens/Admin/Document';
import {useLicense} from "./hooks/useLicense";
import * as Constants from './utils/constants'
import Process from './screens/Admin/Process';
import ProcessDetails from './screens/Admin/ProcessDetails';

function App() {
  return (
    <div className="app h-screen">
      <RecoilRoot>
        <Router>
          <AuthenticationProvider
            configuration={oidcConfig}
            loggerLevel={oidcLog.ERROR}
            isEnabled={true}
            UserStore={WebStorageStateStore}
            callbackComponentOverride={Oidc}
            notAuthenticated={NotAuthenticated}
            notAuthorized={() => { return (<h1>Not Authorized.</h1>) }}
            authenticating={Oidc}
          >
            <Routes>
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/missing-license" element={<MissingLicense />} />
              <Route path="/not-found" element={<NotFound />} />

              {/* Everything else is the admin area, gated behind sign-in. A nested <Routes>
                  is required here because v6 only allows <Route> as a direct child of <Routes>. */}
              <Route path="/*" element={
                <OidcSecure>
                  <SetAuthToken>
                    <SetOidcRoutesContext>
                      {/* Admin routes */}
                      <WhiteLabelProvider configuration={{
                        centerpointApiUrl: config.centerpointBaseUrl
                      }}>
                        <AdminRoutes />
                      </WhiteLabelProvider>
                    </SetOidcRoutesContext>
                  </SetAuthToken>
                </OidcSecure>
              } />
            </Routes>
          </AuthenticationProvider>
        </Router>
        <Toaster />
      </RecoilRoot>
    </div>
  )
}

function AdminRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AdminLayoutRoute nav="home" component={Home} featureEnabled={true} />} />
      <Route path="/home" element={<AdminLayoutRoute nav="home" component={Home} featureEnabled={true} />} />
      <Route path="/send" element={<AdminLayoutRoute nav="send" component={Send} licenseId={Constants.licenses.smartForms} featureEnabled={true} />} />
      <Route path="/send/:dialogKey" element={<AdminLayoutRoute nav="sendDialogkey" component={SendFlow} featureEnabled={true} />} />
      <Route path="/document" element={<AdminLayoutRoute nav="document" component={Document} featureEnabled={true} />} />
      <Route path="/view/smartforms" element={<AdminLayoutRoute nav="smartforms" component={Process} featureEnabled={true} />} />
      <Route path="/view/smartforms/:dialogKey" element={<AdminLayoutRoute nav="smartformsDialogKey" component={ProcessDetails} featureEnabled={true} />} />
      <Route path="/view/smartforms/:dialogKey/:step" element={<AdminLayoutRoute nav="smartformsDialogKeyStep" component={ProcessDetails} featureEnabled={true} />} />
    </Routes>
  )
}

function AdminLayoutRoute({
  component: Component,
  featureEnabled,
  nav,
  subMenuNav,
  licenseId,
}) {

  const { hasLicense } = useLicense();

  return (
    <ProtectedRoute hasLicense={hasLicense(licenseId)} featureEnabled={featureEnabled}>
      <AdminLayout nav={nav} subMenuNav={subMenuNav}>
        <Component />
      </AdminLayout>
    </ProtectedRoute>
  )
}

const ProtectedRoute = ({ featureEnabled, hasLicense, children }) => {
  return featureEnabled && hasLicense ? (
    <>
      {children}
    </>
  ) :
    (<Navigate to={'/'} replace />)
}

const SetOidcRoutesContext = ({ children }) => {
  const loadAction = useToastAction();
  const [userProfile, setUserProfile] = useState({});
  const [customerInfo, setCustomerInfo] = useState({});

  const loadUserContextData = async () => {
    loadAction.execute(async () => {
      const userProfilePromise = getUserProfile();
      const customerPromise = getCustomerInfo();
      const res = await Promise.all([userProfilePromise, customerPromise]);
      setUserProfile(res[0]);
      setCustomerInfo(res[1]);
    }, "Failed to load user data");
  }

  useEffect(() => {
    loadUserContextData();
  }, []);

  return !loadAction.isExecuting && (
    <OidcRoutesContext.Provider
      value={{
        userProfile,
        customerInfo
      }}>
      {children}
    </OidcRoutesContext.Provider>
  )
}

/**
 * Small route wrapper that gets the oidc user and sets the axios auth header 
 */
const SetAuthToken = ({ children }) => {
  const { oidcUser, events } = useReactOidc();

  setAuthHeader(oidcUser.access_token)

  useEffect(() => {
    if (!events) return
    events.addUserSignedOut(addUserSignedOut);
    return () => {
      events.removeUserSignedOut(addUserSignedOut);
    };
  }, [events])

  const addUserSignedOut = () => {
    const userManager = getUserManager()
    userManager.removeUser();
    window.location.reload(false);
  }

  return (
    <>
      {children}
    </>
  );
}

// Exported so App.test.js can render the admin route table directly, without also standing up
// the real OIDC provider chain that wraps it here.
export { AdminRoutes };
export default App;
